import {
  Component,
  ElementRef,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { LucideChevronDown, LucideDynamicIcon, provideLucideIcons } from '@lucide/angular';
import { AppShellService } from '../../core/services/app-shell.service';
import { FeatureFlagsService } from '../../core/services/feature-flags.service';
import { LayoutPreferenceService } from '../../core/services/layout-preference.service';
import { ThemeService } from '../../core/services/theme.service';
import { groupUserMenuItems } from '../../core/utils/navigation.util';
import { UiAvatarComponent } from '../avatar/ui-avatar.component';
import { UiMenuItemComponent } from '../menu-item/ui-menu-item.component';

@Component({
  selector: 'ui-user-menu',
  standalone: true,
  imports: [UiAvatarComponent, UiMenuItemComponent, LucideDynamicIcon],
  providers: [provideLucideIcons(LucideChevronDown)],
  template: `
    <div class="ui-user-menu" [class.ui-user-menu--open]="open()">
      <button
        type="button"
        class="ui-user-menu__trigger"
        (click)="toggle()"
        [attr.aria-expanded]="open()"
        aria-haspopup="menu"
      >
        <ui-avatar
          [src]="shell.userAvatar"
          [name]="shell.userName"
          [alt]="shell.userName"
          size="sm"
          variant="topbar"
        />
        <span class="ui-user-menu__name">{{ shell.userName }}</span>
        <svg lucideIcon="chevron-down" [size]="16" class="ui-user-menu__chevron" [class.ui-user-menu__chevron--open]="open()"></svg>
      </button>

      @if (open()) {
        <div class="ui-user-menu__panel" role="menu">
          <div class="ui-user-menu__header">
            <div class="ui-user-menu__header-profile">
              <ui-avatar
                [src]="shell.userAvatar"
                [name]="shell.userName"
                [alt]="shell.userName"
                size="md"
                variant="brand"
              />
              <div class="ui-user-menu__header-text">
                <span class="ui-user-menu__header-kicker">Minha conta</span>
                <span class="ui-user-menu__header-name">{{ shell.userName }}</span>
                @if (shell.userEmail) {
                  <span class="ui-user-menu__header-email">{{ shell.userEmail }}</span>
                }
              </div>
            </div>
            <button type="button" class="ui-user-menu__header-action" (click)="goToProfile()">
              Ver perfil
            </button>
          </div>

          @for (section of menuSections(); track section.id) {
            <div class="ui-user-menu__section">
              <p class="ui-user-menu__section-label">{{ section.label }}</p>
              <div class="ui-user-menu__section-items">
                @for (entry of section.entries; track entry.key) {
                  <ui-menu-item
                    [label]="entry.label"
                    [icon]="entry.icon"
                    [badge]="entry.badge"
                    (activated)="onEntryClick(entry)"
                  />
                }
              </div>
            </div>
          }

          <div class="ui-user-menu__section ui-user-menu__section--compact">
            <ui-menu-item
              [label]="theme.mode() === 'dark' ? 'Tema claro' : 'Tema escuro'"
              [icon]="theme.mode() === 'dark' ? 'sun' : 'moon'"
              (activated)="toggleTheme()"
            />
            @if (showLayoutToggle) {
              <ui-menu-item label="Voltar ao layout clássico" variant="muted" (activated)="revertToClassic()" />
            }
          </div>

          <div class="ui-user-menu__footer">
            <ui-menu-item label="Sair" icon="log-out" variant="danger" (activated)="logout()" />
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: inline-flex;
      align-items: center;
      align-self: center;
    }

    .ui-user-menu { position: relative; }
    .ui-user-menu__trigger {
      display: inline-flex; align-items: center; gap: 0.5rem;
      padding: 0.25rem 0.5rem 0.25rem 0.25rem;
      border: 1px solid transparent; border-radius: 999px;
      background: none; cursor: pointer;
      color: var(--text-primary, #1f1f1f);
      transition: background 0.15s, border-color 0.15s;
      max-width: 14rem;
    }
    .ui-user-menu__trigger:hover,
    .ui-user-menu--open .ui-user-menu__trigger {
      background: var(--surface-muted, #f5f5f5);
      border-color: var(--border-color, #e8e8e8);
    }
    .ui-user-menu__name {
      font-size: 0.875rem; font-weight: 500;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      display: none;
    }
    @media (width >= 40rem) { .ui-user-menu__name { display: inline; } }
    .ui-user-menu__chevron {
      color: var(--text-muted, #8c8c8c); flex-shrink: 0;
      transition: transform 0.15s; stroke: currentColor;
    }
    .ui-user-menu__chevron--open { transform: rotate(180deg); }
    .ui-user-menu__panel {
      position: absolute; top: calc(100% + 0.5rem); right: 0; z-index: 60;
      width: min(18.5rem, calc(100vw - 2rem));
      overflow: hidden; border-radius: 0.75rem;
      border: 1px solid var(--border-color, #e8e8e8);
      background: var(--surface-bg, #fff); color: var(--text-primary, #1f1f1f);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.14);
    }
    .ui-user-menu__header {
      padding: 1rem;
      background: linear-gradient(
        145deg,
        color-mix(in srgb, var(--brand-primary, #1677ff) 12%, var(--surface-bg, #fff)) 0%,
        var(--surface-muted, #f8fafc) 55%,
        var(--surface-bg, #fff) 100%
      );
      border-bottom: 1px solid var(--border-color, #e8e8e8);
    }
    .ui-user-menu__header-profile { display: flex; align-items: center; gap: 0.75rem; min-width: 0; }
    .ui-user-menu__header-text { display: flex; flex-direction: column; gap: 0.125rem; min-width: 0; }
    .ui-user-menu__header-kicker {
      font-size: 0.6875rem; font-weight: 600;
      letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-muted, #8c8c8c);
    }
    .ui-user-menu__header-name {
      font-size: 0.9375rem; font-weight: 700; color: var(--text-primary, #1f1f1f);
      line-height: 1.25; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .ui-user-menu__header-email {
      font-size: 0.75rem; color: var(--text-secondary, #595959);
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .ui-user-menu__header-action {
      margin-top: 0.75rem; width: 100%;
      padding: 0.4375rem 0.75rem; border-radius: 0.375rem;
      border: 1px solid color-mix(in srgb, var(--brand-primary, #1677ff) 35%, var(--border-color, #e8e8e8));
      background: var(--surface-bg, #fff); color: var(--brand-primary, #1677ff);
      font-size: 0.8125rem; font-weight: 600; cursor: pointer;
      transition: background 0.15s, border-color 0.15s;
    }
    .ui-user-menu__header-action:hover {
      background: color-mix(in srgb, var(--brand-primary, #1677ff) 8%, var(--surface-bg, #fff));
      border-color: var(--brand-primary, #1677ff);
    }
    .ui-user-menu__section { padding: 0.5rem; }
    .ui-user-menu__section--compact {
      padding-top: 0; border-top: 1px solid var(--border-color, #f0f0f0);
    }
    .ui-user-menu__section-label {
      margin: 0 0 0.375rem; padding: 0 0.5rem;
      font-size: 0.6875rem; font-weight: 600;
      letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-muted, #8c8c8c);
    }
    .ui-user-menu__section-items { display: flex; flex-direction: column; gap: 0.125rem; }
    .ui-user-menu__footer {
      padding: 0.5rem; border-top: 1px solid var(--border-color, #f0f0f0);
      background: var(--surface-muted, #fafafa);
    }
    :host-context(.ui-topbar--primary) .ui-user-menu__trigger {
      color: var(--topbar-fg, var(--on-brand-primary));
      border-color: var(--topbar-border, var(--on-brand-primary-border));
      background: var(--topbar-surface, var(--on-brand-primary-subtle));
    }
    :host-context(.ui-topbar--primary) .ui-user-menu__trigger:hover,
    :host-context(.ui-topbar--primary) .ui-user-menu--open .ui-user-menu__trigger {
      background: var(--topbar-surface-hover, var(--on-brand-primary-hover));
      border-color: var(--topbar-border, var(--on-brand-primary-border));
    }
    :host-context(.ui-topbar--primary) .ui-user-menu__chevron {
      color: var(--topbar-fg-muted, var(--on-brand-primary-muted));
    }
  `],
})
export class UiUserMenuComponent {
  shell = inject(AppShellService);
  private router = inject(Router);
  private host = inject(ElementRef<HTMLElement>);
  private featureFlags = inject(FeatureFlagsService);
  private layoutPref = inject(LayoutPreferenceService);
  theme = inject(ThemeService);

  readonly open = signal(false);

  get showLayoutToggle(): boolean {
    return this.featureFlags.isEnabled('ui.layout_v2');
  }

  menuSections(): { id: string; label: string; entries: { key: string; label: string; icon?: string; link?: string; badge?: string }[] }[] {
    return groupUserMenuItems(this.shell.routes, (item) => this.shell.canShow(item))
      .map((section) => ({
        id: section.id,
        label: section.label,
        entries: section.entries
          .filter((item) => item.link !== '/perfil')
          .map((item) => ({
            key: item.link,
            label: item.label,
            icon: item.icon,
            link: item.link,
            badge:
              item.link === '/novidades' && this.shell.totalUnread > 0
                ? String(this.shell.totalUnread)
                : undefined,
          })),
      }))
      .filter((section) => section.entries.length > 0);
  }

  toggle(): void { this.open.update((v) => !v); }
  close(): void { this.open.set(false); }

  goToProfile(): void {
    this.shell.goToAccount();
    this.close();
  }

  onEntryClick(entry: { link?: string }): void {
    if (entry.link) this.router.navigateByUrl(entry.link);
    this.close();
  }

  toggleTheme(): void {
    this.theme.toggle();
    this.close();
  }

  revertToClassic(): void {
    this.layoutPref.disable();
    window.location.reload();
  }

  logout(): void {
    this.close();
    void this.shell.logout();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void { this.close(); }
}
